<template>
  <div
    v-if="!isSingle"
    class="bag-filter"
  >
    <div class="order-options-items">
      <div
        v-if="persons"
        class="order-options-items--peoples"
      >
        <p class="order-label">
          Portioner
        </p>
        <p class="radio-wrapper">
          <template
            v-for="person in persons"
          >
            <input
              :id="'persons-' + person"
              :key="'input-persons-' + person"
              v-model="selectedPersons"
              :value="person"
              :name="'persons'"
              type="radio"
            >
            <label
              :key="'label-persons-' + person"
              :for="'persons-' + person"
            >{{ person }}</label>
          </template>
        </p>
      </div>
      <div
        v-if="days"
        class="order-options-items--days"
      >
        <p class="order-label">
          Dagar
        </p>
        <p class="radio-wrapper">
          <template
            v-for="day in days"
          >
            <input
              :id="'day-target-' + day"
              :key="'day-' + day"
              v-model="selectedDays"
              :value="day"
              :name="'day-target-' + day"
              type="radio"
              :disabled="!isDaysEnabled(day)"
            >
            <label
              :key="'day-label-' + day"
              :for="'day-target-' + day"
            >{{ day }}</label>
          </template>
        </p>
      </div>
      <div
        v-if="bag.products.some(p => p.glutenFree)"
        class="order-options-items--gluten filter-item"
      >
        <div class="checkbox">
          <input
            id="glutenFreeFilter"
            v-model="glutenFree"
            type="checkbox"
            class="checkbox__input"
            :disabled="!hasGlutenFreeOption"
          >
          <label
            for="glutenFreeFilter"
            class="checkbox__label"
          >Gluten- och laktosfri</label>
        </div>
      </div>
      <div
        v-if="bag.products.some(p => p.lactoseFree)"
        class="order-options-items--lactose filter-item"
      >
        <div class="checkbox">
          <input
            id="lactoseFreeFilter"
            v-model="lactoseFree"
            type="checkbox"
            class="checkbox__input"
            :disabled="!hasLactoseFreeOption"
          >
          <label
            for="lactoseFreeFilter"
            class="checkbox__label"
          >Laktosfri</label>
        </div>
      </div>
      <div
        v-if="bag.products.some(p => p.vegetarian) && !allVegetarian"
        class="order-options-items--vegetarian filter-item"
      >
        <div class="checkbox">
          <input
            id="vegetarianFreeFilter"
            v-model="vegetarian"
            type="checkbox"
            class="checkbox__input"
            :disabled="!hasVegetarianOption"
          >
          <label
            for="vegetarianFreeFilter"
            class="checkbox__label"
          >Vego</label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    bag: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      glutenFree: null,
      lactoseFree: null,
      selectedDays: null,
      selectedPersons: null,
      vegetarian: null
    }
  },
  computed: {
    allVegetarian () {
      return this.bag.products.filter(p => !!p.vegetarian).length == this.bag.products.length
    },
    days () {
      const allDays = []
      for(const { days } of this.bag.products) {
        Number.isInteger(days) && allDays.push(days)
      }
      return new Set(allDays.sort())
    },
    hasGlutenFreeOption () {
      return this.bag.products.filter(p => !!p.glutenFree).length > 0
    },
    hasLactoseFreeOption () {
      return this.bag.products.filter(p => !!p.lactoseFree).length > 0
    },
    hasVegetarianOption () {
      return this.bag.products.filter(p => !!p.vegetarian).length > 0
    },
    persons () {
      const allPeoples = []
      for(const { peoples } of this.bag.products) {
        Number.isInteger(peoples) && allPeoples.push(peoples)
      }
      return new Set(allPeoples.sort())
    },
    isSingle () {
      return this.bag.products.length == 1
    },
  },
  watch: {
    glutenFree: function () {
      this.selectedProductChanged()
    },
    lactoseFree: function () {
      this.selectedProductChanged()
    },
    selectedDays: function () {
      this.selectedProductChanged()
    },
    selectedPersons: function (val) {
      if (this.isDaysEnabled(this.selectedDays)) {
        this.selectedProductChanged()
        return
      }

      // set selectedDays to the first item that has the current number of persons
      this.selectedDays = this.bag.products.filter(p => p.peoples === val)[0].days
    },
    vegetarian: function () {
      this.selectedProductChanged()
    }
  },
  mounted () {
    const cheapest = this.bag.products.reduce(
      (prev, curr) => prev.recommendedPriceHomeDelivery < curr.recommendedPriceHomeDelivery ? prev : curr)

    // Prioritize data from the URL when setting up the selected
    // persons, days, other filters.

    if (window.location.search) {
      const params = new URLSearchParams(window.location.search)
      if (params.has('d')) {this.selectedDays = parseInt(params.get('d'), 10)}
      if (params.has('p')) {this.selectedPersons = parseInt(params.get('p'), 10)}
      if (params.has('l')) {this.lactoseFree = params.get('l') == 'true'}
      if (params.has('g')) {this.glutenFree = params.get('g') == 'true'}
      if (params.has('v')) {this.vegetarian = params.get('v') == 'true'}
    }

    this.selectedDays = this.selectedDays || cheapest.days
    this.selectedPersons = this.selectedPersons || cheapest.peoples

    // if we have a single product only for this bag, take attributes
    // from that
    if (this.bag.products.length == 1) {
      const onlyBag = this.bag.products[0]
      this.selectedDays = onlyBag.days
      this.selectedPersons = onlyBag.peoples
      this.lactoseFree = onlyBag.lactoseFree
      this.glutenFree = onlyBag.glutenFree
      this.vegetarian = onlyBag.vegetarian
    } else {
      if (this.allVegetarian) {
        this.vegetarian = true
      }
    }
  },
  methods: {
    isDaysEnabled (days) {
      if (this.selectedPersons === null) {
        return true
      }

      let prods = this.bag.products.filter(
        p => p.peoples === this.selectedPersons)

      if (this.glutenFree !== null) {
        prods = prods.filter(p => p.glutenFree === !!this.glutenFree)
      }

      if (this.lactoseFree !== null) {
        prods = prods.filter(p => p.lactoseFree === !!this.lactoseFree)
      }

      if (this.vegetarian !== null) {
        prods = prods.filter(p => p.vegetarian === !!this.vegetarian)
      }

      return prods.some(p => p.days === days)
    },
    isPeoplesEnabled (peoples) {
      if (this.selectedDays === null) {
        return true
      }

      let prods = this.bag.products.filter(
        p => p.days === this.selectedDays)

      if (this.glutenFree !== null) {
        prods = prods.filter(p => p.glutenFree === !!this.glutenFree)
      }

      if (this.lactoseFree !== null) {
        prods = prods.filter(p => p.lactoseFree === !!this.lactoseFree)
      }

      if (this.vegetarian !== null) {
        prods = prods.filter(p => p.vegetarian === !!this.vegetarian)
      }

      return prods.some(p => p.peoples === peoples)
    },
    selectedProductChanged () {
      if (!this.selectedPersons || !this.selectedDays) {
        return
      }

      const products = this.bag.products.filter(
        p => p.peoples === this.selectedPersons
          && p.days === this.selectedDays
          && p.glutenFree === !!this.glutenFree
          && p.lactoseFree === !!this.lactoseFree
          && p.vegetarian === !!this.vegetarian)

      if (products.length > 0) {
        this.$emit('productChanged', products[0])
      }
    }
  }
}
</script>

<style lang="scss">
.bag-filter {
  color: $color-brown-dark;
  margin-bottom: 0.5rem;

  .order-options-items {
    display: flex;
    flex-flow: row wrap;

    &--peoples,
    &--days {
      margin-right: 1.5rem;
    }

    &--lactose,
    &--gluten,
    &--vegetarian,
    &--discount {
      align-self: flex-end;
      margin: 0 1rem 0.4rem 0;

      @include media-breakpoint-up(lg) {
        margin-bottom: 0;
      }
    }

    &--discount {
      margin-bottom: 1.3rem;
      display: flex;

      @include media-breakpoint-up(lg) {
        margin-bottom: 0.4rem;
      }
    }
  }

  .order-label {
    font-weight: bold;
    margin-bottom: 0.5rem;
    margin-top: 0;
  }

  .radio-wrapper {
    display: flex;
    position: relative;
    margin: 0 0 1rem;

    @include media-breakpoint-up(lg) {
      margin-bottom: 0;
    }

    input[type="radio"] {
      opacity: 0;
      position: absolute;
      margin: 0;
      top: 0;
      left: 0;
      width: 0;
      height: 0;
    }

    label {
      padding: 0.25rem 1rem;
      border: 1px solid $color-grey-light;
      background-color: $color-white;

      &:not(:first-of-type) {
        border-left: none;
      }

      @include font(text, 3 "black");

      &:first-of-type {
        border-radius: 0.25rem 0 0 0.25rem;
      }

      &:last-of-type {
        border: 1px solid $color-grey-light;
        border-radius: 0 0.25rem 0.25rem 0;
        margin-left: -1px;
      }

      &:only-of-type {
        border-radius: 0.25rem;
      }
    }

    [type="radio"]:checked + label {
      background: $color-red;
      color: $color-white;
      border: 1px solid $color-red;
      border-right: 0;
      margin-right: 1px;
    }

    [type="radio"]:disabled + label {
      background: $color-white;
      color: $color-grey-light;
      border: 1px solid $color-grey-light;
      cursor: not-allowed;
    }
  }

  .checkbox {
    margin-left: -0.375rem;
    margin-bottom: 0.5rem;

    @include media-breakpoint-up(lg) {
      margin-bottom: 0;
    }

    &__label::before {
      background: $color-white;
    }

    &__label {
      margin-left: 0.25rem;

      @include font(text, 3, "normal");
    }
  }
}
</style>
